/**
 * AddressFormTemplate
 *
 * Shipping & billing address collection for the GLP-1 quiz workflow.
 * Wraps the reusable ShippingBillingAddressStep component.
 */

import { useState } from 'react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'
import ShippingBillingAddressStep, { type AddressFields } from '@/components/intake/ShippingBillingAddressStep'

interface AddressFormTemplateProps {
  stage: WorkflowStage
  answers: Record<string, unknown>
  currentPhase?: PhaseInfo | null
  onSelect: (value: AnswerValue) => void
}

export default function AddressFormTemplate({
  stage,
  answers,
  currentPhase,
  onSelect,
}: AddressFormTemplateProps) {
  const existingData = (answers.shipping_billing_data || {}) as Record<string, unknown>

  const [shippingAddress, setShippingAddress] = useState<AddressFields>({
    addressLine1: (existingData.shippingAddressLine1 as string) || '',
    addressLine2: (existingData.shippingAddressLine2 as string) || '',
    city: (existingData.shippingCity as string) || '',
    state: (existingData.shippingState as string) || '',
    postalCode: (existingData.shippingPostalCode as string) || '',
  })

  const [billingAddress, setBillingAddress] = useState<AddressFields>({
    addressLine1: (existingData.billingAddressLine1 as string) || '',
    addressLine2: (existingData.billingAddressLine2 as string) || '',
    city: (existingData.billingCity as string) || '',
    state: (existingData.billingState as string) || '',
    postalCode: (existingData.billingPostalCode as string) || '',
  })

  const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(
    existingData.billingSameAsShipping !== undefined
      ? Boolean(existingData.billingSameAsShipping)
      : true
  )

  const headerVar = stage.templateVariables?.find(v => v.title === 'header')
  const subHeaderVar = stage.templateVariables?.find(v => v.title === 'subHeader')

  const handleShippingChange = (field: keyof AddressFields, value: string) => {
    setShippingAddress(prev => {
      const updated = { ...prev, [field]: value }
      emitValue(updated, billingAddress, billingSameAsShipping)
      return updated
    })
  }

  const handleBillingChange = (field: keyof AddressFields, value: string) => {
    setBillingAddress(prev => {
      const updated = { ...prev, [field]: value }
      emitValue(shippingAddress, updated, billingSameAsShipping)
      return updated
    })
  }

  const handleSameToggle = (val: boolean) => {
    setBillingSameAsShipping(val)
    if (val) {
      setShippingAddress({ ...billingAddress })
      emitValue(billingAddress, billingAddress, true)
    } else {
      emitValue(shippingAddress, billingAddress, false)
    }
  }

  const emitValue = (ship: AddressFields, bill: AddressFields, same: boolean) => {
    onSelect({
      billingAddressLine1: bill.addressLine1,
      billingAddressLine2: bill.addressLine2,
      billingCity: bill.city,
      billingState: bill.state,
      billingPostalCode: bill.postalCode,
      shippingAddressLine1: same ? bill.addressLine1 : ship.addressLine1,
      shippingAddressLine2: same ? bill.addressLine2 : ship.addressLine2,
      shippingCity: same ? bill.city : ship.city,
      shippingState: same ? bill.state : ship.state,
      shippingPostalCode: same ? bill.postalCode : ship.postalCode,
      billingSameAsShipping: same,
    })
  }

  return (
    <div className="flex flex-col gap-4 pb-6">
      {(headerVar || subHeaderVar) && (
        <div className="mb-2">
          {headerVar && (
            <h2 className="text-xl font-semibold text-white mb-1">
              {headerVar.value as string}
            </h2>
          )}
          {subHeaderVar && (
            <p className="text-sm text-white/60">{subHeaderVar.value as string}</p>
          )}
        </div>
      )}

      <ShippingBillingAddressStep
        shippingAddress={shippingAddress}
        billingAddress={billingAddress}
        billingSameAsShipping={billingSameAsShipping}
        onShippingChange={handleShippingChange}
        onBillingChange={handleBillingChange}
        onBillingSameToggle={handleSameToggle}
        variant="dark"
      />
    </div>
  )
}
